import { useEffect, useRef } from "react";
import { useDebateStore } from "../store/debateStore";

/**
 * Audio-driven pseudo-lipsync — drives a single CSS custom property
 * (`--lip-amp` ∈ [0, 1]) on the returned ref so consumers can express
 * the visual "mouth movement" purely in CSS, with zero React re-renders
 * per frame.
 *
 * Why a ref + CSS variable (not `useState`)?
 *   The TTS analyser ticks at the display refresh rate (~60 fps). Pushing
 *   that signal through React's render pipeline would invalidate the
 *   speaker-spotlight subtree 60×/sec. Writing a CSS variable on a single
 *   DOM node is O(1), GPU-friendly, and keeps the component tree quiet.
 *
 * SIGNAL EXTRACTION
 *   The Web Audio analyser exposes a frequency-domain magnitude buffer
 *   (`getByteFrequencyData`). Vocal energy concentrates in the lower
 *   bins (rough fundamental + first formants ≈ 80–1500 Hz). We average
 *   the first `VOCAL_BIN_COUNT` bins, normalise to [0, 1] and apply an
 *   exponential moving average so the visual reads as "breathing speech"
 *   instead of jittery noise.
 *
 *   ema_t = (1-α)·ema_{t-1} + α·sample      α = 0.35  (responsive but
 *                                                       not flickery)
 *
 *   The smoothed value is clamped to [0, 1] and written to `--lip-amp`.
 *
 * USAGE
 *   const ref = useLipsyncRef(isAudible);
 *   <div ref={ref} className="lip-jaw">…avatar surface…</div>
 *
 *   …with a CSS rule like:
 *   .lip-jaw {
 *     transform-origin: 50% 35%;
 *     transform: scaleY(calc(1 + 0.06 * var(--lip-amp, 0)))
 *                translateY(calc(0.6% * var(--lip-amp, 0)));
 *     filter: brightness(calc(1 + 0.10 * var(--lip-amp, 0)));
 *     transition: transform 60ms linear, filter 60ms linear;
 *   }
 *
 * Stops cleanly when `active` flips to false or the analyser is torn
 * down (audio session ended, speaker silent).
 */
export function useLipsyncRef<T extends HTMLElement = HTMLDivElement>(
  active: boolean,
): React.RefObject<T> {
  const analyser = useDebateStore((s) => s.ttsAnalyser);
  const ref = useRef<T>(null);
  const rafRef = useRef<number>(0);
  const smoothRef = useRef<number>(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Inactive / no analyser → reset and bail. The default in CSS is 0
    // so the avatar settles back to its resting frame.
    if (!active || !analyser) {
      el.style.setProperty("--lip-amp", "0");
      smoothRef.current = 0;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      return;
    }

    const buf = new Uint8Array(analyser.frequencyBinCount);
    // Cap at 16 bins so very large FFT sizes don't dilute the vocal band.
    // With the engine's standard fftSize=512 and 24 kHz sample rate the
    // first 16 bins cover ≈ 0–750 Hz — exactly the speech fundamental.
    const VOCAL_BIN_COUNT = Math.min(16, buf.length);
    const ALPHA = 0.35; // EMA factor — see header comment

    const tick = () => {
      analyser.getByteFrequencyData(buf);
      let sum = 0;
      for (let i = 0; i < VOCAL_BIN_COUNT; i++) {
        sum += buf[i] ?? 0;
      }
      // Normalise: avg byte / 255 → [0, 1]
      const sample = sum / VOCAL_BIN_COUNT / 255;
      smoothRef.current = smoothRef.current * (1 - ALPHA) + sample * ALPHA;
      // Clamp defensively (FFT data is byte-bounded so avg ≤ 1, but a
      // numeric guard costs nothing and prevents pathological NaNs from
      // ever reaching paint).
      const clamped =
        smoothRef.current < 0
          ? 0
          : smoothRef.current > 1
          ? 1
          : smoothRef.current;
      el.style.setProperty("--lip-amp", clamped.toFixed(3));
      rafRef.current = requestAnimationFrame(tick);
    };

    tick();
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      // Restore the resting frame so a paused / muted speaker doesn't
      // freeze mid-syllable.
      el.style.setProperty("--lip-amp", "0");
    };
  }, [analyser, active]);

  return ref;
}
